import { isOnlineOrRecent } from '@utils/isOnlineOrRecent'
import { formatDistance, isBefore, subDays } from 'date-fns'
import { colors, fontSize, size } from '@styles/theme'
import { useAuthContext } from '@contexts/authContext'
import styled from '@emotion/styled'
import clamp from 'clamp-js'

import { StatusIcon } from '@elements/StatusIndicator'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  z-index: 1;
  animation: fadeInFromBottom 250ms ease-in;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 ${size(1)};
  padding-top: 0;
`

const Bubble = styled.div`
  border-radius: 100px;
  padding: ${size(0.75)} ${size(1.5)};
  display: flex;
  align-items: center;
  background-color: #333333;
  margin-left: ${size(0.35)};
  box-shadow: 0px 4px 6px rgba(0,0,0,0.5);
  font-size: ${fontSize('sm')};
  font-weight: bold;
  margin-bottom: ${size(0.5)};
`

const TopBubble = styled(Bubble)`
  height: ${size(3.75)};
  white-space: nowrap;
`

const OwnBubble = styled(Bubble)`
  border-top-left-radius: 25px;
  padding: ${size(1.5)} ${size(2.25)};
  margin: 0;
  margin-bottom: ${size(0.75)};
  min-width: 60dvw;
  font-size: ${fontSize('sm')};
  font-weight: normal;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${size(0.75)};
`

const TopbarScrollThresholdAnchor = styled.div`
  position: relative;
  top: ${size(-12)};
`

const ActiveDot = styled.div`
  height: ${size(1.5)};
  width: ${size(1.5)};
  margin-right: ${size(0.5)};
  border-radius: 100px;
  background-color: ${colors.onlineIndicator()};
`

function isOver14DaysOld(targetDate) {
  const fourteenDaysAgo = subDays(new Date(), 14);
  return isBefore(targetDate, fourteenDaysAgo);
}

const formatTime = (dateTime) => {
  try {
    if (isOver14DaysOld(dateTime)) {
      return 'offline'
    }
    let time = formatDistance(dateTime, Date.now())
    time = time.replace('about ', '')
    if (time === 'less than a minute') {
      return '< 1 min'
    } else if (time.slice(time.length - 6) === 'minute') {
      return time.replace('minute', 'min')
    } else if (time.slice(time.length - 7) === 'minutes') {
      return time.replace('minutes', 'mins')
    } else if (time.slice(time.length - 4) === 'hour') {
      return time.replace('hour', 'hr')
    } else if (time.slice(time.length - 5) === 'hours') {
      return time.replace('hours', 'hrs')
    } else {
      return time
    }
  } catch {
    return ''
  }
}

const ProfileUserBrief = ({ user }) => {
  const { state: authState } = useAuthContext()
  const isCurrentUser = user?.nearbyUuid && authState?.user?.nearbyUuid === user?.nearbyUuid
  // TODO: handle case where user is not present

  const renderDate = (dateTime) => {
    if (!dateTime) return;
    const { isOnline, isRecent } = isOnlineOrRecent(user)
    if (isCurrentUser || isOnline) {
      return (
        <>
          <ActiveDot />
          online
        </>
      )
    } else if (isRecent) {
      return (
        <>
          <ActiveDot style={{ backgroundColor: colors.recentIndicator() }} />
          {formatTime(dateTime)}
        </>
      )
    } else {
      return formatTime(dateTime)
    }
  }

  const renderTempStatus = () => {
    if (user?.tempStatus === 'right_now' && (user?.tempStatusMeta?.position || user?.tempStatusMeta?.hosting)) {
      return `${user?.tempStatusMeta?.position} - ${user?.tempStatusMeta?.hosting}`.replaceAll('_', ' ')
    } else {
      return user?.tempStatus?.replaceAll('_', ' ')
    }
  }

  return (
    <Container>
      <TopbarScrollThresholdAnchor id='nearby-profile__brief-top' />
      <Row>
        <TopBubble>
          <IconContainer>
            <StatusIcon
              status={user?.tempStatus}
              size={size(2.25)}
            />
          </IconContainer>
          {renderTempStatus()}
        </TopBubble>
        <TopBubble>
          {renderDate(user?.lastSeenAt)}
        </TopBubble>
      </Row>
      <Row>
        {user?.extendedInfos?.tagline && user?.extendedInfos?.tagline.replaceAll(' ', '').length > 0 && 
          <OwnBubble>
            <span ref={node => node && clamp(node, { clamp: 3 })}>
              {user?.extendedInfos?.tagline}
            </span>
          </OwnBubble>
        }
      </Row>
    </Container>
  )
}

export default ProfileUserBrief
